import { useLexicalEditable } from '@lexical/react/useLexicalEditable'
import {
    $applyNodeReplacement,
    $getNodeByKey,
    createCommand,
    DecoratorNode,
    IS_BOLD,
    IS_HIGHLIGHT,
    IS_ITALIC,
    IS_STRIKETHROUGH,
    type DOMConversionMap,
    type DOMExportOutput,
    type LexicalEditor,
    type NodeKey,
} from 'lexical'
import type { JSX } from 'react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { ReferenceChip } from './ReferenceChip'
import { parseReferenceTokens, type TextReference } from './referenceModel'

type SerializedReferenceNode = {
    type: 'dynamic-reference'
    version: 1
    raw: string
    reference: TextReference
    format?: number
}

export const OPEN_REFERENCE_TARGET_PICKER_COMMAND = createCommand<NodeKey>('OPEN_REFERENCE_TARGET_PICKER_COMMAND')

export class ReferenceNode extends DecoratorNode<JSX.Element> {
    __raw: string
    __reference: TextReference
    __format: number

    constructor(raw: string, reference: TextReference, format = 0, key?: NodeKey) {
        super(key)
        this.__raw = raw
        this.__reference = reference
        this.__format = format
    }

    static getType() {
        return 'dynamic-reference'
    }

    static clone(node: ReferenceNode) {
        return new ReferenceNode(node.__raw, node.__reference, node.__format, node.__key)
    }

    static importJSON(serialized: SerializedReferenceNode) {
        return $createReferenceNode(serialized.raw, serialized.reference, serialized.format)
    }

    static importDOM(): DOMConversionMap {
        return {
            span: (element) => {
                const raw = element.getAttribute('data-reference-token')
                const token = raw && parseReferenceTokens(raw)[0]
                if (!token || token.raw !== raw) {
                    return null
                }
                const format = Number(element.dataset.referenceFormat) || 0
                return { conversion: () => ({ node: $createReferenceNode(raw, token.reference, format) }), priority: 2 }
            },
        }
    }

    exportJSON(): SerializedReferenceNode {
        return { type: 'dynamic-reference', version: 1, raw: this.__raw, reference: this.__reference, format: this.__format }
    }

    createDOM() {
        const element = document.createElement('span')
        element.dataset.referenceToken = this.__raw
        element.dataset.referenceFormat = String(this.__format)
        return element
    }

    updateDOM() {
        return false
    }

    exportDOM(): DOMExportOutput {
        const element = this.createDOM()
        element.textContent = this.__raw
        return { element }
    }

    decorate(editor: LexicalEditor) {
        return (
            <EditableReferenceChip
                editor={editor}
                nodeKey={this.getKey()}
                raw={this.__raw}
                reference={this.__reference}
                format={this.__format}
            />
        )
    }

    getTextContent() {
        return this.__raw
    }

    getReference() {
        return this.getLatest().__reference
    }

    getReferenceFormat() {
        return this.getLatest().__format
    }

    isInline() {
        return true
    }

    isKeyboardSelectable() {
        return false
    }
}

function EditableReferenceChip({
    editor,
    nodeKey,
    raw,
    reference,
    format,
}: {
    editor: LexicalEditor
    nodeKey: NodeKey
    raw: string
    reference: TextReference
    format: number
}) {
    const isEditable = useLexicalEditable()
    return (
        <ReferenceChip
            raw={raw}
            reference={reference}
            textStyle={
                format
                    ? {
                          fontWeight: format & IS_BOLD ? tokens.TextSEmphasis.fontWeight : undefined,
                          fontStyle: format & IS_ITALIC ? 'italic' : undefined,
                          textDecoration: format & IS_STRIKETHROUGH ? 'line-through' : undefined,
                          backgroundColor: format & IS_HIGHLIGHT ? tokens.ColorSurfaceAccentLimeDefault : undefined,
                      }
                    : undefined
            }
            focusable={false}
            onResolve={isEditable ? () => editor.dispatchCommand(OPEN_REFERENCE_TARGET_PICKER_COMMAND, nodeKey) : undefined}
            onDelete={
                isEditable
                    ? () =>
                          editor.update(() => {
                              $getNodeByKey(nodeKey)?.remove()
                          })
                    : undefined
            }
        />
    )
}

export function $createReferenceNode(raw: string, reference: TextReference, format = 0) {
    return $applyNodeReplacement(new ReferenceNode(raw, reference, format))
}

export function $isReferenceNode(node: unknown): node is ReferenceNode {
    return node instanceof ReferenceNode
}
